// shared/components/LogicalPluginsModal.jsx
import React, { useState, useEffect } from "react";
import Modal from "./Modal.jsx";
import { SYSTEMS, getCapabilitiesBySite, getSystemsBySite } from "../../lib/logicalCanvas/pluginRegistry.js";
import { resolveEffectivePolicy, fetchUpstreamPolicy } from "../../lib/logicalCanvas/policyEngine.js";
import { loadSelection, saveSelection } from "../../lib/logicalCanvas/storage.js";

export default function LogicalPluginsModal({ onClose, activeSite }) {
  const [selectedSystemId, setSelectedSystemId] = useState(null);
  const [effectivePolicy, setEffectivePolicy] = useState({});
  const [localSelection, setLocalSelection] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function init() {
      const upstream = await fetchUpstreamPolicy();
      const local = loadSelection();
      // CRITICAL: Filter capabilities by activeSite only
      const registry = getCapabilitiesBySite(activeSite);
      const effective = resolveEffectivePolicy(upstream, local, registry);
      
      setEffectivePolicy(effective);
      setLocalSelection(local);
      setLoading(false);
      
      // Set first system of the site as default
      const siteSystems = getSystemsBySite(activeSite);
      if (siteSystems.length > 0 && !selectedSystemId) {
        setSelectedSystemId(siteSystems[0].systemId);
      }
    }
    init();
  }, [activeSite]);

  const handleToggle = (capabilityId, currentPolicy) => {
    if (currentPolicy.locked) return;

    const newEnabled = !currentPolicy.enabled;
    const newLocal = { ...localSelection };
    newLocal[capabilityId] = {
      enabled: newEnabled,
      mode: currentPolicy.mode,
    };
    setLocalSelection(newLocal);

    // Update effective policy immediately for UI feedback
    // CRITICAL: Use site-scoped capabilities only
    const registry = getCapabilitiesBySite(activeSite);
    const upstream = {}; // Would fetch again in production
    const newEffective = resolveEffectivePolicy(upstream, newLocal, registry);
    setEffectivePolicy(newEffective);
  };

  const handleSave = () => {
    saveSelection(localSelection);
    onClose();
  };

  // CRITICAL: Only show systems for the active site
  const siteSystems = getSystemsBySite(activeSite);
  const selectedSystem = siteSystems.find(s => s.systemId === selectedSystemId);

  return (
    <Modal
      title={`Authority Portal — Manage Capabilities (${activeSite.toUpperCase()})`}
      onClose={onClose}
      footer={
        <>
          <button className="chip px-4 py-2" onClick={onClose}>
            Cancel
          </button>
          <button className="chip px-4 py-2 bg-[#2a4456] hover:bg-[#345566]" onClick={handleSave}>
            Save & Apply
          </button>
        </>
      }
    >
      {loading ? (
        <div className="text-mist text-center py-8">Loading policies...</div>
      ) : (
        <div className="flex gap-6" style={{ minHeight: "400px" }}>
          {/* Left: Systems List */}
          <div className="w-48 border-r border-edge pr-4">
            <div className="text-mist text-sm uppercase tracking-wider mb-3">
              Systems ({activeSite})
            </div>
            <div className="space-y-1">
              {siteSystems.length === 0 ? (
                <div className="text-mist/60 text-sm italic py-4">
                  No systems available for {activeSite}
                </div>
              ) : (
                siteSystems.map(system => (
                  <button
                    key={system.systemId}
                    onClick={() => setSelectedSystemId(system.systemId)}
                    className={`w-full text-left px-3 py-2 rounded transition-colors ${
                      selectedSystemId === system.systemId
                        ? "bg-[#1a2c36] text-ice border border-white/10"
                        : "text-mist hover:bg-[#0e1b22]"
                    }`}
                  >
                    {system.systemName}
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Right: Capabilities for Selected System */}
          <div className="flex-1">
            <div className="mb-3">
              <h3 className="text-ice font-semibold text-lg">{selectedSystem?.systemName}</h3>
              <p className="text-mist/80 text-sm">{selectedSystem?.description}</p>
            </div>

            <div className="space-y-2">
              {selectedSystem?.capabilities.map(capability => {
                const policy = effectivePolicy[capability.id] || {
                  enabled: false,
                  mode: capability.accessDefault,
                  locked: false,
                  source: "default",
                };

                const isLocked = policy.locked;
                const isEnabled = policy.enabled;

                return (
                  <div
                    key={capability.id}
                    className={`flex items-center justify-between p-3 rounded border ${
                      isLocked
                        ? "bg-[#0a1318] border-edge/50 opacity-60"
                        : "bg-[#0e1b22] border-edge"
                    }`}
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <input
                        type="checkbox"
                        checked={isEnabled}
                        disabled={isLocked}
                        onChange={() => handleToggle(capability.id, policy)}
                        className={`h-4 w-4 rounded border-white/20 ${
                          isLocked ? "cursor-not-allowed" : "cursor-pointer"
                        }`}
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className={isLocked ? "text-mist/60" : "text-ice"}>
                            {capability.label}
                          </span>
                          {isLocked && (
                            <svg className="h-3 w-3 text-mist/50" viewBox="0 0 16 16" fill="currentColor">
                              <path d="M8 1a3 3 0 0 0-3 3v2H4a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V7a1 1 0 0 0-1-1h-1V4a3 3 0 0 0-3-3zm1 5V4a1 1 0 1 0-2 0v2h2z"/>
                            </svg>
                          )}
                        </div>
                        <p className="text-mist/70 text-xs mt-0.5">{capability.description}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <span
                        className={`text-xs px-2 py-1 rounded ${
                          policy.mode === "read-write"
                            ? "bg-[#1f3d29] text-[#7fd199]"
                            : policy.mode === "read-only"
                            ? "bg-[#2d3540] text-[#9db4ce]"
                            : "bg-[#3d2020] text-[#d19999]"
                        }`}
                      >
                        {policy.mode}
                      </span>
                      {isLocked && (
                        <span className="text-xs text-mist/50">(enforced)</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {selectedSystem?.capabilities.length === 0 && (
              <div className="text-mist/70 text-sm text-center py-8">
                No capabilities defined for this system.
              </div>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}
